import React from 'react';
import Seo from '../components/Seo';
import { FileText, Clock, Euro, Shield, AlertTriangle, CheckCircle, Lock } from 'lucide-react';

const AGBPage = () => {
  return (
    <div className="min-h-screen bg-stone-50">
      <Seo 
        title="AGB - Fliesen Jarek | Allgemeine Geschäftsbedingungen"
        description="Allgemeine Geschäftsbedingungen von Fliesen Jarek. Bedingungen für Fliesenlegerarbeiten und Handwerksleistungen."
        keywords="AGB, Allgemeine Geschäftsbedingungen, Fliesen Jarek, Handwerk, Vertragsbedingungen"
      />
      
      <div className="max-w-4xl mx-auto px-6 py-20">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-light text-stone-800 mb-6">
            Allgemeine Geschäftsbedingungen
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            Geltende Bedingungen für alle Handwerksleistungen von Fliesen Jarek
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-12">
          {/* Geltungsbereich */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <FileText className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">§1 Geltungsbereich</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <p>
                Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge zwischen Jarek Jozwiak (nachfolgend "Auftragnehmer") und seinen Auftraggebern über handwerkliche Leistungen, insbesondere:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Fliesenlegerarbeiten aller Art</li>
                <li>Badsanierungen und Renovierungen</li>
                <li>Natursteinverlegung</li>
                <li>Reparatur- und Instandsetzungsarbeiten</li>
                <li>Beratungsleistungen</li>
              </ul>
              <p>
                Abweichende Bedingungen des Auftraggebers werden nur wirksam, wenn sie vom Auftragnehmer ausdrücklich schriftlich bestätigt werden.
              </p>
            </div>
          </div>

          {/* Vertragsschluss */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <CheckCircle className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">§2 Vertragsschluss</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <p>
                <strong className="text-stone-800">Kostenvoranschlag:</strong> Alle Kostenvoranschläge sind unverbindlich und kostenfrei, sofern nicht anders vereinbart.
              </p>
              <p>
                <strong className="text-stone-800">Angebot:</strong> Verbindliche Angebote sind 30 Tage gültig. Der Vertrag kommt durch schriftliche Auftragsbestätigung oder Arbeitsbeginn zustande.
              </p>
              <p>
                <strong className="text-stone-800">Änderungen:</strong> Änderungen am Auftrag bedürfen der Schriftform und können Auswirkungen auf Preis und Ausführungsdauer haben.
              </p>
            </div>
          </div>

          {/* Leistungsumfang */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <Shield className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">§3 Leistungsumfang</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <div>
                <h3 className="font-semibold text-stone-800 mb-2">Inbegriffene Leistungen:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Fachgerechte Ausführung nach VOB und anerkannten Regeln der Technik</li>
                  <li>Verwendung hochwertiger Materialien namhafter Hersteller</li>
                  <li>Baustellen-Sicherheitsmaßnahmen</li>
                  <li>Fachgerechte Entsorgung von Bauschutt (nach Vereinbarung)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-stone-800 mb-2">Nicht inbegriffene Leistungen:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Lieferung von Fliesen und Materialien (außer vereinbart)</li>
                  <li>Elektro- und Sanitärarbeiten</li>
                  <li>Malerarbeiten</li>
                  <li>Möbel- und Einrichtungsgegenstände bewegen</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Preise und Zahlungsbedingungen */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <Euro className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">§4 Preise und Zahlungsbedingungen</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <div>
                <h3 className="font-semibold text-stone-800 mb-2">Preise:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Alle Preise verstehen sich zzgl. der gesetzlichen Mehrwertsteuer</li>
                  <li>Preisänderungen bei Materialpreissteigerungen über 5% werden weitergegeben</li>
                  <li>Zusatzarbeiten werden nach Aufwand berechnet</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-stone-800 mb-2">Zahlungsmodalitäten:</h3>
                <div className="bg-primary-50 p-4 rounded-lg space-y-2">
                  <p><strong>Anzahlung:</strong> 30% bei Auftragserteilung</p>
                  <p><strong>Abschlagszahlungen:</strong> Nach Baufortschritt</p>
                  <p><strong>Schlussrechnung:</strong> Nach vollständiger Fertigstellung</p>
                  <p><strong>Zahlungsziel:</strong> 14 Tage netto ohne Abzug</p>
                </div>
              </div>
              <p>
                <strong className="text-stone-800">Verzug:</strong> Bei Zahlungsverzug werden Verzugszinsen in Höhe von 9 Prozentpunkten über dem Basiszinssatz berechnet.
              </p>
            </div>
          </div>

          {/* Ausführungsfristen */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <Clock className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">§5 Ausführungsfristen</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <p>
                <strong className="text-stone-800">Termine:</strong> Genannte Termine sind Richtwerte. Verbindliche Termine bedürfen einer gesonderten schriftlichen Vereinbarung.
              </p>
              <p>
                <strong className="text-stone-800">Verzögerungen:</strong> Verzögerungen durch höhere Gewalt, Witterung, Lieferengpässe oder vom Auftraggeber zu vertretende Umstände führen zu entsprechender Terminverschiebung.
              </p>
              <div className="bg-amber-50 p-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-1" />
                  <div>
                    <p className="font-medium text-amber-800">Wichtiger Hinweis:</p>
                    <p className="text-amber-700 text-sm">
                      Der Auftraggeber hat die Baustelle rechtzeitig und in vereinbartem Zustand zur Verfügung zu stellen.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gewährleistung */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <Shield className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">§6 Gewährleistung</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-3">5 Jahre Garantie auf alle Arbeiten</h3>
                <p className="text-green-700">
                  Wir gewähren auf alle handwerklichen Leistungen eine Garantie von 5 Jahren ab Abnahme.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-800 mb-2">Gewährleistungsausschluss:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Natürliche Abnutzung und Verschleiß</li>
                  <li>Schäden durch unsachgemäße Behandlung oder Pflege</li>
                  <li>Schäden durch Dritte oder höhere Gewalt</li>
                  <li>Verwendung vom Auftraggeber gestellter Materialien</li>
                </ul>
              </div>
              <p>
                <strong className="text-stone-800">Mängelanzeige:</strong> Mängel sind unverzüglich, spätestens innerhalb von 7 Tagen nach Entdeckung, schriftlich anzuzeigen.
              </p>
            </div>
          </div>

          {/* Haftung */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <AlertTriangle className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">§7 Haftung</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <p>
                <strong className="text-stone-800">Haftungsumfang:</strong> Der Auftragnehmer haftet nur für Schäden, die auf Vorsatz oder grobe Fahrlässigkeit zurückzuführen sind.
              </p>
              <p>
                <strong className="text-stone-800">Versicherungsschutz:</strong> Alle Arbeiten sind durch Betriebshaftpflicht- und Bauleistungsversicherung abgesichert.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-800 text-sm">
                  <strong>Hinweis:</strong> Bei Arbeiten an bestehenden Anlagen wird keine Haftung für verdeckte Mängel oder Schäden übernommen, die bei der Auftragserteilung nicht erkennbar waren.
                </p>
              </div>
            </div>
          </div>

          {/* Eigentumsvorbehalt */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <Lock className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">§8 Eigentumsvorbehalt</h2>
            </div>
            <div className="text-stone-600 leading-relaxed">
              <p>
                Gelieferte Materialien bleiben bis zur vollständigen Bezahlung aller Forderungen Eigentum des Auftragnehmers. Bei vertragswidrigem Verhalten des Auftraggebers ist der Auftragnehmer zur Rücknahme berechtigt.
              </p>
            </div>
          </div>

          {/* Schlussbestimmungen */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <div className="flex items-center mb-6">
              <FileText className="w-6 h-6 text-primary-600 mr-3" />
              <h2 className="text-2xl font-semibold text-stone-800">§9 Schlussbestimmungen</h2>
            </div>
            <div className="text-stone-600 leading-relaxed space-y-4">
              <p>
                <strong className="text-stone-800">Rechtswahl:</strong> Es gilt ausschließlich deutsches Recht unter Ausschluss des UN-Kaufrechts.
              </p>
              <p>
                <strong className="text-stone-800">Gerichtsstand:</strong> Ausschließlicher Gerichtsstand ist Cuxhaven, soweit der Auftraggeber Kaufmann ist.
              </p>
              <p>
                <strong className="text-stone-800">Salvatorische Klausel:</strong> Sollten einzelne Bestimmungen unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
              </p>
              <div className="bg-stone-50 p-4 rounded-lg">
                <p className="text-sm text-stone-600">
                  <strong>Verbraucherschutz:</strong> Für Verbraucher gelten die gesetzlichen Bestimmungen des BGB, soweit diese für den Verbraucher günstiger sind.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-16 pt-8 border-t border-stone-200">
          <div className="space-y-2">
            <p className="text-sm text-stone-500">
              Stand: {new Date().toLocaleDateString('de-DE', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <p className="text-xs text-stone-400">
              Bei Fragen zu den AGB kontaktieren Sie uns gerne unter 0151 54610847
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AGBPage; 